import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Tag, Table, Tabs } from 'antd';
import { APPOINTMENT_STATUSES } from 'constants/appointments';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import { ColumnProps } from 'antd/lib/table';
import {
  LegacyAppointment,
  fetchAppointments
} from 'services/api/appointments';

import { PageWrapper } from 'components/ui';
import { getIdStart } from 'utils/string';
import { showRestError } from 'utils/showErrorMessage';
import { CreateInvoiceModal, HowItWorksLink } from 'components/shared';
import DownloadInvoice from './DownloadInvoice';

const PAGE_LIMIT = 10;

enum TabsValues {
  all = 'all',
  invoiced = 'invoiced',
  notInvoiced = 'notInvoiced'
}

const Invoices: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') || 1);
  const tab = searchParams.get('tab');

  const [appointments, setAppointments] = useState<LegacyAppointment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tab) {
      searchParams.set('tab', TabsValues.notInvoiced);
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, tab]);

  const invoiced = useMemo(() => {
    switch (tab) {
      case TabsValues.invoiced:
        return 1;
      case TabsValues.notInvoiced:
        return 0;
      default:
        return undefined;
    }
  }, [tab]);

  useEffect(() => {
    setLoading(true);
    // #21: rows carry doctor_invoice; ?invoiced=1|0 drives the tabs
    fetchAppointments({
      status: APPOINTMENT_STATUSES.completed,
      invoiced,
      page,
      limit: PAGE_LIMIT
    })
      .then(res => {
        setAppointments(res.results);
        setTotal(res.total);
      })
      .catch(err => showRestError(err))
      .finally(() => setLoading(false));
  }, [page, tab, invoiced]);

  const onTabChange = (key: string) => {
    searchParams.set('tab', key);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const columns: ColumnProps<LegacyAppointment>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: (id: string) => getIdStart(id)
      },
      {
        title: t('appointments.patient_name'),
        dataIndex: 'patient',
        key: 'patient',
        render: value => (
          <Link
            to={`/patients/${value?.id}`}
          >{`${value?.first_name} ${value?.last_name}`}</Link>
        )
      },
      {
        title: t('appointments.date_time'),
        dataIndex: 'dates',
        key: 'dates',
        render: (value: { date: string; accepted: boolean }[]) => {
          const dateItem = value.find(item => item.accepted);

          if (dateItem) {
            return dayjs(dateItem.date).format(FULL_DATE_FORMAT);
          }

          return '-';
        }
      },
      {
        title: t('appointments.status'),
        dataIndex: 'doctor_invoice',
        key: 'doctor_invoice',
        render: value => (
          <Tag color={value ? 'success' : 'error'}>
            {t(`invoices.${value ? 'invoiced' : 'not_invoiced'}`)}
          </Tag>
        )
      },
      {
        title: t('appointments.payment'),
        dataIndex: 'admin_paid',
        key: 'admin_paid',
        render: (value: boolean) => (
          <Tag color={value ? 'success' : 'error'}>
            {value ? t('appointments.paid') : t('appointments.not_paid')}
          </Tag>
        )
      },
      {
        title: '',
        dataIndex: 'doctor_invoice',
        key: 'actions',
        width: '5%',
        align: 'right',
        render(value, record) {
          if (value) {
            return <DownloadInvoice id={record.id} file={value.file} />;
          }

          return <CreateInvoiceModal appointmentId={record.id} />;
        }
      }
    ],
    [t]
  );

  if (!tab) {
    return null;
  }

  return (
    <PageWrapper
      title={t('invoices.invoices')}
      color="white"
      extra={[
        <HowItWorksLink
          key="how-it-works"
          type={DOCTOR_HOW_IT_WORKS_PAGES.INVOICES.key}
          title={t('common.how_it_works')}
        />
      ]}
    >
      <Tabs
        destroyInactiveTabPane
        onChange={onTabChange}
        activeKey={tab}
        items={[
          {
            label: t('common.all'),
            key: TabsValues.all
          },
          {
            label: t('invoices.not_invoiced'),
            key: TabsValues.notInvoiced
          },
          {
            label: t('invoices.invoiced'),
            key: TabsValues.invoiced
          }
        ]}
      />
      <Table
        dataSource={appointments}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total: total || 0,
          current: page,
          onChange: p => {
            searchParams.set('page', `${p}`);
            setSearchParams(searchParams);
          }
        }}
        scroll={{ x: 768 }}
      />
    </PageWrapper>
  );
};

export default Invoices;
